// "Spot the Slip" — error-correction practice items for the calm practice deck.
//
// Each item is one Italian sentence containing exactly ONE deliberate,
// pedagogically meaningful mistake — the kind an English-speaking A1/A2 learner
// of Italian actually makes — paired with the corrected sentence and a one-line
// reason. These map onto the Reference Shelf articles (essere/stare, prepositions,
// gender, piacere, passato/imperfetto, pronoun order, congiuntivo, auxiliaries),
// so the practice and the reference reinforce each other.
//
// The diff between `wrong` and `right` is computed at render time (word-level),
// so we only need the two full sentences plus metadata here. `tag` groups items
// for the filter chips; `slug` (optional) deep-links to the matching article.
//
// Authored for Azzurro · Italian Reference. Each row:
//   [ wrong, right, tag, why, slug ]

// Per-theme expansion rows (identity … media) live in standalone
// corrections-<theme>.js modules and are appended to BASE_ROWS below.
import { EXTRA_CORRECTION_ROWS } from "./corrections-expansion.js";

const BASE_ROWS = [
  // ---- essere vs stare ------------------------------------------------
  ["Oggi sto molto stanco.", "Oggi sono molto stanco.", "essere-stare",
    "Italian uses essere with stanco; stare is reserved for health idioms.", "essere-vs-stare"],
  ["Come sei? Tutto bene?", "Come stai? Tutto bene?", "essere-stare",
    "Asking how someone feels uses stare → come stai.", "essere-vs-stare"],
  ["Sono molto bene, grazie.", "Sto molto bene, grazie.", "essere-stare",
    "Well-being uses stare → sto bene.", "essere-vs-stare"],
  ["Dove sta la stazione?", "Dov'è la stazione?", "essere-stare",
    "Location of a place uses essere → dov'è.", "essere-vs-stare"],
  ["Stai attento, la strada è pericolosa.", "Stai attento, la strada è pericolosa.", "essere-stare",
    "Trap check: 'stare attento' correctly uses stare. (Already correct!)", "essere-vs-stare"],

  // ---- prepositions (di / da / a) -------------------------------------
  ["Il treno parte di Milano alle nove.", "Il treno parte da Milano alle nove.", "preposition",
    "Origin (from a place) uses da, not di.", "di-vs-da"],
  ["Vengo da Parigi, è la mia città.", "Vengo da Parigi, è la mia città.", "preposition",
    "Trap check: origin uses da. (Already correct!)", "di-vs-da"],
  ["Vado a il mare questo fine settimana.", "Vado al mare questo fine settimana.", "articulated-prep",
    "a + il contracts to al.", "preposition-contractions"],
  ["Parliamo di il film più tardi.", "Parliamo del film più tardi.", "articulated-prep",
    "di + il contracts to del.", "preposition-contractions"],
  ["Le chiavi di la macchina sono qui.", "Le chiavi della macchina sono qui.", "articulated-prep",
    "di + la contracts to della.", "preposition-contractions"],

  // ---- noun gender & agreement ----------------------------------------
  ["Ho un grande problema con la mappa.", "Ho un grande problema con la mappa.", "gender",
    "Trap check: problema is masculine and mappa feminine. (Already correct!)", "gender-of-nouns"],
  ["La problema è molto difficile.", "Il problema è molto difficile.", "gender",
    "Greek-origin -ma words like problema are masculine → il.", "gender-of-nouns"],
  ["Mi piace la mano destro.", "Mi piace la mano destra.", "gender",
    "Mano is feminine even though it ends in -o, so the adjective is destra.", "gender-of-nouns"],
  ["È una giorno bellissimo.", "È un giorno bellissimo.", "gender",
    "Giorno is masculine; pair it with un, not una.", "gender-of-nouns"],
  ["Le case bianchi sono nuove.", "Le case bianche sono nuove.", "agreement",
    "Adjectives agree in gender and number: feminine plural → bianche.", "adjective-agreement"],

  // ---- piacere & similar ---------------------------------------------
  ["Io piaccio il caffè.", "Mi piace il caffè.", "piacere",
    "Piacere means 'to be pleasing': the coffee pleases me → mi piace il caffè.", "piacere-and-similar-verbs"],
  ["Mi piace i libri di storia.", "Mi piacciono i libri di storia.", "piacere",
    "The verb agrees with the thing liked; plural books → piacciono.", "piacere-and-similar-verbs"],
  ["Mi piace correre e nuotare, ma non mi piacciono ballare.",
    "Mi piace correre e nuotare, ma non mi piace ballare.", "piacere",
    "With an action (infinitive) piacere stays singular → piace.", "piacere-and-similar-verbs"],
  ["Ti piaci il cioccolato, vero?", "Ti piace il cioccolato, vero?", "piacere",
    "Don't conjugate piacere to the person; match it to the thing (il cioccolato).", "piacere-and-similar-verbs"],

  // ---- passato prossimo vs imperfetto --------------------------------
  ["Quando ero bambino, sono andato al mare ogni estate.",
    "Quando ero bambino, andavo al mare ogni estate.", "passato-imperfetto",
    "A repeated habit in the past is imperfetto → andavo.", "passato-prossimo-vs-imperfetto"],
  ["Ieri ho mangiato con la famiglia e poi uscivo al cinema.",
    "Ieri ho mangiato con la famiglia e poi sono uscito al cinema.", "passato-imperfetto",
    "Single, finished events in sequence take the passato prossimo.", "passato-prossimo-vs-imperfetto"],
  ["Erano le tre quando suonava il telefono.",
    "Erano le tre quando ha suonato il telefono.", "passato-imperfetto",
    "Background time stays imperfetto; the interrupting event is passato prossimo.", "passato-prossimo-vs-imperfetto"],

  // ---- object-pronoun order ------------------------------------------
  ["Il libro? Gli lo ho dato stamattina.", "Il libro? Gliel'ho dato stamattina.", "pronoun-order",
    "gli + lo combine into glielo (gliel'ho).", "object-pronouns"],
  ["Me un caffè porta, per favore.", "Portami un caffè, per favore.", "pronoun-order",
    "On a command the pronoun attaches to the end: portami.", "object-pronouns"],
  ["Lo mi puoi spiegare di nuovo?", "Me lo puoi spiegare di nuovo?", "pronoun-order",
    "Indirect before direct: me lo, never lo me.", "object-pronouns"],

  // ---- congiuntivo presente ------------------------------------------
  ["Voglio che vieni alla festa.", "Voglio che tu venga alla festa.", "congiuntivo",
    "A wish about someone else (volere che) triggers the subjunctive → venga.", "congiuntivo-presente"],
  ["È importante che tu sei puntuale.", "È importante che tu sia puntuale.", "congiuntivo",
    "Impersonal expressions of importance take the subjunctive → sia.", "congiuntivo-presente"],
  ["Spero che hai un buon viaggio.", "Spero che tu abbia un buon viaggio.", "congiuntivo",
    "Sperare che (hope/wish) flips the verb to the subjunctive → abbia.", "congiuntivo-presente"],

  // ---- compound-tense auxiliaries ------------------------------------
  ["Ho andato al lavoro in bici.", "Sono andato al lavoro in bici.", "auxiliary",
    "Andare takes essere, not avere, in compound tenses → sono andato.", "compound-tenses-and-auxiliaries"],
  ["Maria ha venuto tardi alla riunione.", "Maria è venuta tardi alla riunione.", "auxiliary",
    "Venire takes essere; the participle agrees → è venuta.", "compound-tenses-and-auxiliaries"],

  // ---- common A1 mix-ups ---------------------------------------------
  ["Ho visto niente di interessante.", "Non ho visto niente di interessante.", "negation",
    "Italian keeps non before the verb even with niente.", "negation"],
  ["Quanti libri hai? Ho tre.", "Quanti libri hai? Ne ho tre.", "ci-ne",
    "With a quantity use ne ('of them') → ne ho tre.", "ci-and-ne"],
  ["Mi piace caffè italiano.", "Mi piace il caffè italiano.", "article",
    "Italian needs the definite article with general nouns → il caffè.", "definite-and-indefinite-articles"],
];

// Stable id + structured shape for the view.
const ROWS = [...BASE_ROWS, ...EXTRA_CORRECTION_ROWS];
export const CORRECTIONS = ROWS.map(([wrong, right, tag, why, slug], i) => ({
  id: `corr-${i + 1}`,
  wrong,
  right,
  tag,
  why,
  slug: slug || "",
}));

// Tag registry — drives the filter chips. Order is intentional (mirrors the
// Reference Shelf), label is human-readable.
export const CORRECTION_TAGS = [
  { id: "essere-stare", label: "Essere vs Stare" },
  { id: "preposition", label: "Prepositions" },
  { id: "articulated-prep", label: "Articulated Prepositions" },
  { id: "gender", label: "Gender" },
  { id: "agreement", label: "Agreement" },
  { id: "piacere", label: "Piacere" },
  { id: "passato-imperfetto", label: "Passato vs Imperfetto" },
  { id: "pronoun-order", label: "Pronoun Order" },
  { id: "congiuntivo", label: "Congiuntivo" },
  { id: "auxiliary", label: "Auxiliaries" },
  { id: "ci-ne", label: "Ci & Ne" },
  { id: "article", label: "Articles" },
  { id: "plural", label: "Plurals" },
  { id: "negation", label: "Negation" },
];
